import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import AdminLayout from '../components/AdminLayout'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'
import { useUserProfile } from '../context/UserProfileContext'

type Tab = 'account' | 'security'

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('account')
  const [showEdit, setShowEdit] = useState(false)
  const { user, getUserDisplayName } = useUserProfile()
  const [firstName, setFirstName] = useState(user?.firstName || '')
  const [lastName, setLastName] = useState(user?.lastName || '')
  const [email, setEmail] = useState(user?.email || '')
  const [phone, setPhone] = useState(() => {
    const raw = (user as any)?.phoneNumber || ''
    return raw.startsWith('+44') ? raw.slice(3) : raw
  })

  useEffect(() => {
    setFirstName(user?.firstName || '')
    setLastName(user?.lastName || '')
    setEmail(user?.email || '')
    const rawPhone = (user as any)?.phoneNumber || ''
    setPhone(rawPhone.startsWith('+44') ? rawPhone.slice(3) : rawPhone)
  }, [user])

  const initials = useMemo(() => {
    const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
    if (name) {
      return name
        .split(' ')
        .filter(Boolean)
        .map(part => part[0]?.toUpperCase() || '')
        .join('')
        .slice(0, 2) || 'U'
    }
    return (user?.email || 'User').charAt(0).toUpperCase()
  }, [user])

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div>
          <h1 className='text-xl sm:text-2xl font-bold text-gray-900 break-words'>Hi, {getUserDisplayName()}</h1>
          <p className='text-sm text-gray-500'>You are welcome</p>
        </div>

        <h2 className='text-lg sm:text-xl font-bold text-gray-900'>Settings</h2>

        <div className='grid grid-cols-1 gap-6'>
          <div className='rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-6'>
            <div className='mb-4 flex flex-wrap gap-2 sm:gap-3 border-b border-gray-200 pb-3'>
              <button
                onClick={() => setActiveTab('account')}
                className={`rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold ${
                  activeTab === 'account' ? 'bg-[#0b1f3a] text-white' : 'bg-gray-100 text-gray-800'
                }`}
              >
                Account Settings
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold ${
                  activeTab === 'security' ? 'bg-[#0b1f3a] text-white' : 'bg-gray-100 text-gray-800'
                }`}
              >
                Security
              </button>
            </div>

            {activeTab === 'account' && (
            <div className='flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 text-center sm:text-start'>
              <div className='flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 text-orange-700 font-bold text-lg flex-shrink-0'>
                {initials}
              </div>
              <div className='space-y-2 text-sm text-gray-800 w-full break-words'>
                <p>
                  <span className='font-semibold'>Firstname:</span> {firstName || '—'}
                </p>
                <p>
                  <span className='font-semibold'>Lastname:</span> {lastName || '—'}
                </p>
                <p>
                  <span className='font-semibold'>Email:</span> <span className='font-bold'>{email || '—'}</span>
                </p>
                <p>
                  <span className='font-semibold'>Phone number:</span> <span className='font-bold'>{phone ? `+44 ${phone}` : '—'}</span>
                </p>
                <p>
                  <span className='font-semibold'>Status:</span>{' '}
                  {user ? (
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${
                        user.isActive
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                          : 'bg-red-50 text-red-700 border-red-100'
                      }`}
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  ) : (
                    '—'
                  )}
                </p>
                <button
                  onClick={() => setShowEdit(true)}
                  className='mt-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50'
                >
                  Edit Details
                </button>
              </div>
            </div>
            )}

            {activeTab === 'security' && <AdminManagementPanel currentUserId={user?.id} />}
          </div>
        </div>
      </div>

      {showEdit && (
        <div className='fixed inset-0 z-[60] flex items-stretch justify-end bg-black/40'>
          <div
            className='w-full sm:max-w-lg h-full bg-white shadow-2xl flex flex-col'
            role='dialog'
            aria-modal='true'
          >
            <div className='flex items-center justify-between border-b border-gray-200 px-4 sm:px-6 py-4'>
              <h3 className='text-base font-semibold text-gray-900'>Edit Details</h3>
              <button
                onClick={() => setShowEdit(false)}
                className='h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-800'
              >
                ×
              </button>
            </div>
            <div className='flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4'>
              <div className='space-y-1'>
                <label className='text-xs font-semibold text-gray-700'>First Name</label>
                <input
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400'
                />
              </div>
              <div className='space-y-1'>
                <label className='text-xs font-semibold text-gray-700'>Last Name</label>
                <input
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400'
                />
              </div>
              <div className='space-y-1'>
                <label className='text-xs font-semibold text-gray-700'>Email</label>
                <input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400'
                />
              </div>
              <div className='space-y-1'>
                <label className='text-xs font-semibold text-gray-700'>Phone Number</label>
                <div className='flex'>
                  <div className='flex items-center gap-1 px-2.5 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 shrink-0'>
                    <span className='text-xs'>🇬🇧</span>
                    <span className='text-xs font-medium text-gray-600'>+44</span>
                  </div>
                  <input
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
                    placeholder='7700 900000'
                    className='w-full rounded-r-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400'
                  />
                </div>
              </div>
            </div>
            <div className='border-t border-gray-200 px-4 sm:px-6 py-4'>
              <button
                onClick={() => {
                  setShowEdit(false)
                  // Persist immediately for now to keep UI in sync
                  setFirstName(firstName.trim())
                  setLastName(lastName.trim())
                  setEmail(email.trim())
                  setPhone(phone.trim())
                }}
                className='w-full rounded-md bg-[#0b1f3a] px-4 py-3 text-sm font-semibold text-white hover:bg-[#0a1a31] transition'
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

type AdminRow = {
  id: string
  firstName?: string
  lastName?: string
  email: string
  isActive: boolean
}

/**
 * Super-admin controls: list every admin and activate/deactivate them.
 * Any admin may manage others, but cannot deactivate their own account.
 */
function AdminManagementPanel({ currentUserId }: { currentUserId?: string | undefined }) {
  const [admins, setAdmins] = useState<AdminRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  useEffect(() => {
    const loadAdmins = async () => {
      setLoading(true)
      setError(null)
      try {
        const { response } = await adminService.listUsers({ role: 'admin', limit: 100 })
        const data: any = (response as any)?.data || response
        const items = data?.items || data?.data?.items || []
        setAdmins(
          items.map((u: any) => ({
            id: u.id || u._id,
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            isActive: Boolean(u.isActive),
          }))
        )
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Unable to load admins.')
      } finally {
        setLoading(false)
      }
    }
    void loadAdmins()
  }, [])

  const toggleActive = async (admin: AdminRow) => {
    const next = !admin.isActive
    setTogglingId(admin.id)
    // Optimistic update, rolled back if the request fails.
    setAdmins(prev => prev.map(a => (a.id === admin.id ? { ...a, isActive: next } : a)))
    try {
      await adminService.updateUserStatus(admin.id, { isActive: next })
      const name = `${admin.firstName || ''}`.trim() || 'Admin'
      toast.success(`${name} ${next ? 'activated' : 'deactivated'}.`)
    } catch (err) {
      setAdmins(prev => prev.map(a => (a.id === admin.id ? { ...a, isActive: !next } : a)))
      toast.error(err instanceof ApiError ? err.message : 'Failed to update admin.')
    } finally {
      setTogglingId(null)
    }
  }

  const initialsOf = (a: AdminRow) => {
    const name = `${a.firstName || ''} ${a.lastName || ''}`.trim()
    if (name) {
      return name
        .split(' ')
        .filter(Boolean)
        .map(p => p[0]?.toUpperCase() || '')
        .join('')
        .slice(0, 2)
    }
    return (a.email || 'A').charAt(0).toUpperCase()
  }

  return (
    <div>
      <p className='text-sm text-gray-500 mb-4'>
        Manage admin access. Deactivating an admin blocks them from signing in until reactivated.
      </p>

      {error && (
        <div className='mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>{error}</div>
      )}

      {loading ? (
        <div className='space-y-3'>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className='h-16 rounded-xl bg-gray-100 animate-pulse' />
          ))}
        </div>
      ) : admins.length === 0 ? (
        <div className='rounded-xl border border-gray-100 px-4 py-10 text-center text-sm text-gray-500'>
          No admins found.
        </div>
      ) : (
        <div className='space-y-3'>
          {admins.map(admin => {
            const isSelf = !!currentUserId && admin.id === currentUserId
            const name = `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || admin.email
            return (
              <div
                key={admin.id}
                className='flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-3 sm:flex-row sm:items-center sm:justify-between'
              >
                <div className='flex items-center gap-3 min-w-0'>
                  <div className='flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-700 font-semibold text-sm flex-shrink-0'>
                    {initialsOf(admin)}
                  </div>
                  <div className='min-w-0'>
                    <p className='text-sm font-semibold text-gray-900 truncate'>
                      {name}
                      {isSelf && <span className='ml-2 text-xs font-medium text-gray-400'>(You)</span>}
                    </p>
                    <p className='text-xs text-gray-500 truncate'>{admin.email}</p>
                  </div>
                </div>

                <div className='flex items-center gap-3 self-end sm:self-auto'>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${
                      admin.isActive
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                        : 'bg-red-50 text-red-700 border-red-100'
                    }`}
                  >
                    {admin.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {isSelf ? (
                    <span className='text-xs text-gray-400'>—</span>
                  ) : (
                    <button
                      onClick={() => toggleActive(admin)}
                      disabled={togglingId === admin.id}
                      className={`rounded-lg px-3 py-2 text-xs sm:text-sm font-semibold transition disabled:opacity-50 ${
                        admin.isActive
                          ? 'border border-red-200 text-red-700 hover:bg-red-50'
                          : 'border border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                      }`}
                    >
                      {togglingId === admin.id ? '…' : admin.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
